import React, { useRef, useState, useEffect } from 'react';
import { MDBTable, MDBTableBody, MDBTableHead } from 'mdbreact';
import '../../style/Popup.css';
import '../../style/Print.css';
import {
  fetchSeasonDetails,
  fetchCalendarLeague,
  patchIsMatchDateTimeConfirmed,
} from '../../util/api/requests';
import {
  formatBackendDateEtLocale,
  formatBackendDateTimeEtLocale,
} from '../../util/dateUtil';
import DateTimeOverlayPopover from './DateTimeOverlayPopover';
import { isUserLoggedIn } from '../../components/user/User';
import Form from 'react-bootstrap/Form';
import { renderSpinner } from '../../util/util';
import Col from 'react-bootstrap/Col';
import ReactToPrint from 'react-to-print';
import Button from 'react-bootstrap/Button';
import Row from 'react-bootstrap/Row';

const CalendarPopup = ({ leagueId, loggedInUserTeamId, closeTrigger }) => {
  const componentRef = useRef();

  return (
    <>
      <div className="print-button-wrapper">
        <ReactToPrint
          trigger={() => <Button id="print-button">Prindi</Button>}
          content={() => componentRef.current}
        />
      </div>
      <CalendarPrintWrapper
        leagueId={leagueId}
        loggedInUserTeamId={loggedInUserTeamId}
        closeTrigger={closeTrigger}
        ref={componentRef}
      />
    </>
  );
};

export default CalendarPopup;

const CalendarPrintWrapper = React.forwardRef(
  ({ leagueId, loggedInUserTeamId, closeTrigger }, ref) => {
    const [calendarData, setCalendarData] = useState(null);
    const [season, setSeason] = useState(null);
    const [loading, setLoading] = useState({
      leagueCalendar: true,
      season: true,
    });

    const loadSeason = async (seasonId) => {
      try {
        const seasonDetails = await fetchSeasonDetails(seasonId);
        if (seasonDetails) setSeason(seasonDetails);
      } catch (error) {
        console.error('Error loading season:', error);
      } finally {
        setLoading((prev) => ({ ...prev, season: false }));
      }
    };

    const loadCalendarleague = async (leagueId) => {
      try {
        const calendarLeague = await fetchCalendarLeague(leagueId);
        if (calendarLeague) setCalendarData(calendarLeague);
      } catch (error) {
        console.error('Error calendar league season:', error);
      } finally {
        setLoading((prev) => ({ ...prev, season: false }));
      }
    };
    

    useEffect(() => {
      (async () => {
        try {
          const calendarLeague = await fetchCalendarLeague(leagueId);
          if (calendarLeague) {
            setCalendarData(calendarLeague);
            await loadSeason(calendarLeague.seasonId);
          } else {
            setLoading((prev) => ({ ...prev, season: false }));
          }
        } catch (error) {
          console.error('Error fetching league calendar:', error);
        } finally {
          setLoading((prev) => ({ ...prev, leagueCalendar: false }));
        }
      })();
    }, [leagueId]);

    if (loading.leagueCalendar || loading.season) return renderSpinner();
    if (!calendarData || !calendarData.matchDays || !season) return null;

    const distinctTeams = new Set(
      calendarData.matchDays.map((x) => x.homeTeamId)
    );
    const roundSize = Math.floor(distinctTeams.size / 2);

    function isLoggedInUserInPlayingTeam(matchDay) {
      return (
        matchDay.homeTeamId === loggedInUserTeamId ||
        matchDay.awayTeamId === loggedInUserTeamId
      );
    }

    function isLoggedInUserAdmin() {
      return loggedInUserTeamId === null;
    }

    function updateIsMatchDateTimeConfirmed(isChecked, matchId) {
      patchIsMatchDateTimeConfirmed(matchId, {
        isMatchDateTimeConfirmed: isChecked,
      });
    }

    function renderHeaderRow() {
      return (
        <tr>
          <th>Algusaeg</th>
          <th>Lõppaeg</th>
          <th>Kodumeeskond</th>
          <th>Külalismeeskond</th>
          <th>Toimumise aeg</th>
          {isUserLoggedIn() && <th>Aeg kinnitatud</th>}
          {isUserLoggedIn() && <th />}
        </tr>
      );
    }

    function conditionallyRenderRoundRow(idx, roundSize) {
      if (idx % roundSize === 0) {
        return (
          <tr>
            <td colSpan={4} />
            <td className="font-weight-bold print-style">
              Voor {(idx + roundSize) / roundSize}
            </td>
            {isUserLoggedIn() && <td className="not-to-print" />}
            {isUserLoggedIn() && <td className="not-to-print" />}
          </tr>
        );
      }
    }

    function renderMatchRow(idx, matchDay) {
      return (
        <tr key={idx}>
          <td className="print-style">
            {formatDate(matchDay.matchDayStartDate)}
          </td>
          <td className="print-style">
            {formatDate(matchDay.matchDayEndDate)}
          </td>
          <td className="print-style">{matchDay.homeTeamName}</td>
          <td className="print-style">{matchDay.awayTeamName}</td>
          <td className="print-style">
            {formatDatetime(matchDay.matchDateTime)}
          </td>
          {isUserLoggedIn() && (
            <td className="print-style">
              {renderIsDateTimeConfirmedCheckbox(matchDay, idx)}
            </td>
          )}
          {isUserLoggedIn() && (
            <td className="not-to-print text-left">
              {renderEditDateTime(matchDay)}
            </td>
          )}
        </tr>
      );
    }

    function formatDate(date) {
      return date ? formatBackendDateEtLocale(date) : null;
    }

    function formatDatetime(datetime) {
      return datetime ? formatBackendDateTimeEtLocale(datetime) : null;
    }

    function renderEditDateTime(matchDay) {
      if (!isLoggedInUserInPlayingTeam(matchDay) && !isLoggedInUserAdmin())
        return '';
      return (
        <DateTimeOverlayPopover
          season={season}
          selectedDateTime={matchDay.matchDateTime}
          matchId={matchDay.matchId}
          forceLeagueCalendarUpdate={() => loadCalendarleague(leagueId)}
        />
      );
    }

    function renderIsDateTimeConfirmedCheckbox(matchDay, idx) {
      return (
        <Form.Check
          custom
          type="checkbox"
          id={`confirm-date-time-checkbox-${idx}`}
          label=""
          defaultChecked={matchDay.isMatchDateTimeConfirmed}
          disabled={
            !isLoggedInUserInPlayingTeam(matchDay) && !isLoggedInUserAdmin()
          }
          onChange={(event) =>
            updateIsMatchDateTimeConfirmed(
              event.target.checked,
              matchDay.matchId
            )
          }
        />
      );
    }

    return (
      <div className="home_player" ref={ref}>
        <Row>
          <Col className="text-right">
            <button className="close-modal" onClick={closeTrigger}>
              &times;
            </button>
          </Col>
        </Row>
        <h3 className="to-print text-center">{season.name}</h3>
        <h3 className="to-print text-center">{calendarData.leagueName}</h3>
        <MDBTable striped small className="content">
          <MDBTableHead color="blue" textWhite className="print-style">
            {renderHeaderRow()}
          </MDBTableHead>
          <MDBTableBody>
            {calendarData.matchDays.map((matchDay, idx) => (
              <React.Fragment key={idx}>
                {conditionallyRenderRoundRow(idx, roundSize)}
                {renderMatchRow(idx, matchDay)}
              </React.Fragment>
            ))}
          </MDBTableBody>
        </MDBTable>
      </div>
    );
  }
);
